import dayjs, { Dayjs } from 'dayjs';

import customParseFormat from 'dayjs/plugin/customParseFormat';
import tz from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

dayjs.extend(customParseFormat);
dayjs.extend(utc);
dayjs.extend(tz);

export const convertToMS = (
  time: number | string | Dayjs,
  startOrEnd?: 'start' | 'end'
): number => {
  if (!time) {
    return 0;
  } else if (typeof time === 'string' && startOrEnd && startOrEnd === 'start') {
    return dayjs(time).startOf('day').valueOf();
  } else if (typeof time === 'string' && startOrEnd && startOrEnd === 'end') {
    return dayjs(time).endOf('day').valueOf();
  } else if (typeof time === 'string') {
    return dayjs(time).valueOf();
  } else if (typeof time === 'object') {
    return time.valueOf();
  }

  return time > 9999999999 ? time : time * 1000;
};

export const convertToTimezonedMS = (
  time: number | string | Dayjs,
  timezone: string,
  startOrEnd?: 'start' | 'end'
): number => {
  if (!time) {
    return 0;
  } else if (typeof time === 'string' && startOrEnd && startOrEnd === 'start') {
    return dayjs.tz(time, timezone).startOf('day').valueOf();
  } else if (typeof time === 'string' && startOrEnd && startOrEnd === 'end') {
    return dayjs.tz(time, timezone).endOf('day').valueOf();
  } else if (typeof time === 'string') {
    return dayjs.tz(time, timezone).valueOf();
  } else if (typeof time === 'object') {
    return time.valueOf();
  }

  return time > 9999999999 ? time : time * 1000;
};

const UTC_DAY_BOUND_DATE_FORMATS = ['YYYY-MM-DD', 'YYYY/MM/DD', 'MM/DD/YYYY', 'M/D/YYYY'];

/**
 * Convert a clinic-local date string to the UTC datetime string
 * ('YYYY-MM-DD HH:mm:ss') of that local day's start ('start' -> 00:00:00) or
 * end ('end' -> 23:59:59), for SQL comparisons against UTC DATETIME columns.
 * Only complete dates in UTC_DAY_BOUND_DATE_FORMATS are accepted; anything else
 * -> undefined. Strict parsing prevents dayjs from turning epoch-ms strings or
 * partial dates into unintended bounds.
 */
export const convertToUtcDayBound = (
  time: string | null | undefined,
  timezone: string,
  startOrEnd: 'start' | 'end'
): string | undefined => {
  const parsed = time ? dayjs(time, UTC_DAY_BOUND_DATE_FORMATS, true) : null;

  if (!parsed || !parsed.isValid()) {
    return undefined;
  }

  try {
    return dayjs(convertToTimezonedMS(parsed.format('YYYY-MM-DD'), timezone, startOrEnd))
      .utc()
      .format('YYYY-MM-DD HH:mm:ss');
  } catch {
    return undefined;
  }
};

/**
 * Normalize an issued date (string | number | Dayjs | null/undefined)
 * to clinic start-of-day epoch milliseconds using the provided timezone.
 * - Missing value -> now at clinic start-of-day
 * - String -> clinic start-of-day for that date
 * - Number -> interpret as ms, shift to clinic tz, take start-of-day
 * - Dayjs -> shift to clinic tz, take start-of-day
 */
export const normalizeIssuedDateMs = (
  value: string | number | Dayjs | null | undefined,
  timezone: string
): number => {
  if (value == null) {
    return dayjs().tz(timezone).startOf('day').valueOf();
  }

  if (typeof value === 'string') {
    return convertToTimezonedMS(value, timezone, 'start');
  }

  if (typeof value === 'number') {
    return convertToTimezonedMS(dayjs(value).tz(timezone).startOf('day'), timezone);
  }

  // Dayjs
  return convertToTimezonedMS(value.tz(timezone).startOf('day'), timezone);
};
